import graphql from "babel-plugin-relay/macro";
import { useAtom } from "jotai";
import { useEffect } from "react";
import { useFragment } from "react-relay";

import { BookingWizardDataAtom } from "@/features/BookingWizard/atoms/stores";
import { deriveIsRequest } from "@/features/BookingWizard/utils/deriveIsRequest";
import { VStack } from "@whoosh/design-system/src/components/primitive";

import { LessonBookingDetailsStepNode$key } from "./__generated__/LessonBookingDetailsStepNode.graphql";
import { GroupSizeControl } from "./controls";
import { LessonCourseControl } from "./controls/LessonCourseControl/LessonCourseControl";
import { TimeSelectionControl } from "./controls/TimeSelectionControl/TimeSelectionControl";

type Props = {
  nodeRef: LessonBookingDetailsStepNode$key | null;
  slots: Array<{
    date: string;
    durationMins: number;
    id: string;
    time: string;
  }>;
};

export const LessonBookingDetailsStep = ({ nodeRef, slots }: Props) => {
  const node = useFragment(LessonBookingDetailsStepFragmentString, nodeRef);

  const startSlot = node?.firstStartSlot?.edges?.[0]?.node;

  const isLessonRequest = deriveIsRequest(startSlot?.agenda?.facility);

  const [bookingWizardData, setBookingWizardData] = useAtom(
    BookingWizardDataAtom
  );

  useEffect(() => {
    if (
      bookingWizardData?.type === "LessonBooking" &&
      isLessonRequest &&
      !bookingWizardData.minTime &&
      !bookingWizardData.maxTime &&
      slots.length > 0
    ) {
      setBookingWizardData({
        ...bookingWizardData,
        minTime: slots[0].time,
        maxTime: slots[slots.length - 1].time,
      });
    }
  }, [bookingWizardData, setBookingWizardData, slots, isLessonRequest]);
  return (
    <VStack backgroundColor="surface-secondary" flex={1} maxHeight={500}>
      {isLessonRequest ? <TimeSelectionControl timeSlots={slots} /> : null}
      <VStack backgroundColor="surface-secondary">
        <GroupSizeControl nodeRef={node ?? null} />
      </VStack>
      <LessonCourseControl nodeRef={node ?? null} />
    </VStack>
  );
};

export const LessonBookingDetailsStepFragmentString = graphql`
  fragment LessonBookingDetailsStepNode on Node {
    id
    ...GroupSizeControlNode
    ...LessonCourseControlNode
    ... on AvailabilitySlot {
      firstStartSlot: startSlots(
        first: 1
        slotAvailability: {
          facilityId: $facilityId
          bookingType: LESSON
          lessonTypeId: $lessonTypeId
        }
      ) {
        edges {
          node {
            id
            agenda {
              facility {
                club {
                  supportsPrivate
                }
                privateLessonsPermissionSet {
                  patronAccess
                }
                publicLessonsPermissionSet {
                  patronAccess
                }
              }
            }
          }
        }
      }
    }
  }
`;
